import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import ButtonPublish from '../../../components/_button/ButtonPublish';
import DialogConfirm from '../../../components/_dialog/DialogConfirm';
import { useDeleteMinisitesMutation } from '../../../../graphql/mutation/minisite/deleteMinisitesMutation';
import ButtonDelete from '../../../components/_button/ButtonDelete';
import MinisiteTableItem from './MinisiteTableItem';
import {
  IMinisiteQueryData,
  IMinisiteVars,
  useMinisiteQuery,
} from '../../../../graphql/query/minisite/useMinisiteQuery';
import useNotification from '../../../components/_hook/useNotification';
import ButtonUnpublish from '../../../components/_button/ButtonUnpublish';
import GraphQLTable from '../../../components/_table/GraphQLTable';
import { useUnpublishMinisitesMutation } from '../../../../graphql/mutation/minisite/unpublishMinisitesMutation';
import { usePublishMinisitesMutation } from '../../../../graphql/mutation/minisite/publishMinisitesMutation';
import useRoutes from '../../../components/_hook/useRoutes';
import Helmet from '../../../components/Helmet';
import { IMinisiteSchema } from '../../../../graphql/type/minisite';
import MinisiteLayout from '../../../layout/Minisite';
import Context from '../../../context';

function MinisiteTable() {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { path } = useRoutes();
  const { company, isSuperAdmin } = useContext(Context);
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<IMinisiteSchema[]>([]);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [deleteMinisitesMutation] = useDeleteMinisitesMutation(
    `
    isSuccess
  `,
    {
      onCompleted: () => {
        notification.success(
          t('__item___deleted', {
            item: `${selectedItems.length} ${t('minisite', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
        setTableRefreshKey(new Date().getTime());
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [publishMinisitesMutation] = usePublishMinisitesMutation(
    `updatedMinisites {
				_id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('minisite', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [unpublishMinisitesMutation] = useUnpublishMinisitesMutation(
    `updatedMinisites {
				_id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('minisite', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  function toggleDeleteDialog() {
    setIsOpenDeleteDialog(!isOpenDeleteDialog);
  }

  async function onClickPublish() {
    await publishMinisitesMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  async function onClickUnpublish() {
    await unpublishMinisitesMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  async function onClickDelete() {
    await deleteMinisitesMutation({
      variables: { _ids: selectedItems.map((item) => item._id) },
    });
  }

  return (
    <>
      <Helmet title={`${t('list')} | ${t('minisite')}`} />
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete')}
        open={isOpenDeleteDialog}
        onClose={toggleDeleteDialog}
        onConfirm={onClickDelete}
      />
      <Button
        color="primary"
        variant="contained"
        component={Link}
        to={path('minisite.detail', { docID: 'new', companyID: company._id })}
        startIcon={<AddIcon />}
      >
        {t('new')}
      </Button>
      <GraphQLTable<IMinisiteQueryData, IMinisiteSchema, IMinisiteVars>
        refreshKey={tableRefreshKey}
        select={`
				_id
				company
				status
                domain
       	 `}
        useQuery={useMinisiteQuery}
        renderHeader={[
          {
            field: '_id',
            label: t('id'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'domain',
            label: t('domain'),
            align: 'left',
          },
          {
            field: 'action',
            label: t('action'),
            align: 'right',
          },
        ]}
        renderItem={(item) => (
          <MinisiteTableItem
            item={item}
            setTableRefreshKey={setTableRefreshKey}
          />
        )}
        useCheckbox
        onItemSelected={setSelectedItems}
        selectedItems={selectedItems}
        searchFields={{
          _id: { label: t('id') },
          domain: { label: t('domain') },
        }}
        title={t('minisite')}
        defaultFilterOptions={{
          companyId: company._id,
        }}
        defaultSort="createdTime"
        defaultSortOrder={-1}
        actionButtons={[
          ...(isSuperAdmin
            ? [
                <ButtonPublish
                  key="publish"
                  variant="contained"
                  color="primary"
                  size="small"
                  text={t('publish')}
                  onClick={onClickPublish}
                />,
                <ButtonUnpublish
                  key="unpublish"
                  color="primary"
                  variant="contained"
                  size="small"
                  text={t('unpublish')}
                  onClick={onClickUnpublish}
                />,
              ]
            : []),
          <ButtonDelete
            key="delete"
            variant="contained"
            size="small"
            text={t('delete')}
            onClick={toggleDeleteDialog}
          />,
        ]}
        itemKey="_id"
      />
    </>
  );
}

export default () => <MinisiteLayout main={<MinisiteTable />} />;
